import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Plus } from "lucide-react";
import Button from "../../../components/Buttons/Button";
import { useTranslationApp } from "../../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME,
  TEMPLATES_ADD_LAYER,
  TEMPLATES_DELETE,
  TEMPLATES_LAYERS,
} from "../../../i18n/keysTranslations";
import { findNodePath } from "../utils/layerTree";
import { flattenInstanceItems } from "../instance/instanceGraph";
import SortableNestedLayerList from "./SortableNestedLayerList";

const LayerTreePanel = ({
  nodes = {},
  nodeLayouts = {},
  assets = {},
  selectedID,
  expandedIDs,
  disabled,
  onSelect,
  onToggleExpand,
  onRename,
  onReorder,
  onDelete,
  onAddLayer,
}) => {
  const { t } = useTranslationApp();
  const items = useMemo(
    () => flattenInstanceItems({ nodes, nodeLayouts, assets }),
    [assets, nodeLayouts, nodes]
  );
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );
  const selectedParentID =
    findNodePath({ nodes, nodeID: selectedID })?.parentID || null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E4E4E7",
        borderRadius: "14px",
        p: 2,
        height: "100%",
        minHeight: 360,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Typography sx={{ fontWeight: 500, mb: 1 }}>
        {t(TEMPLATES_LAYERS)}
      </Typography>
      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {items.length ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <SortableNestedLayerList
              nodes={nodes}
              nodeLayouts={nodeLayouts}
              itemsById={itemsById}
              selectedID={selectedID}
              expandedIDs={
                selectedParentID
                  ? { ...expandedIDs, [selectedParentID]: true }
                  : expandedIDs
              }
              disabled={disabled}
              onSelect={onSelect}
              onRename={onRename}
              onToggleExpand={onToggleExpand}
              onReorder={onReorder}
              onDelete={onDelete}
              fallbackName={t(MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME, {
                index: 1,
              })}
              deleteLabel={t(TEMPLATES_DELETE)}
            />
          </Box>
        ) : null}
      </Box>
      <Button
        color="secondary"
        onClick={onAddLayer}
        disabled={disabled}
        startIcon={<Plus size={14} />}
      >
        {t(TEMPLATES_ADD_LAYER)}
      </Button>
    </Box>
  );
};

export default LayerTreePanel;
